import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isSoftConsistencyAllDisabled } from '@/components/project/softConsistencyPanel'
import { AlertTriangle, CheckCircle, Clock, Loader2, Pencil, RefreshCw, RotateCcw, ScanSearch, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { friendlyMessage } from '@/api/http'
import { chapterApi } from '@/api/chapterApi'
import { conflictApi } from '@/api/conflictApi'
import { foreshadowApi } from '@/api/foreshadowApi'
import { projectApi } from '@/api/projectApi'
import { getRuleLabel } from '@/api/ruleLabelsApi'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ALL_PROJECTS_VALUE, ProjectCombobox } from '@/components/project/ProjectCombobox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { Pagination } from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import ConflictContextDialog from '@/components/ConflictContextDialog'
import PartialRegenerateModal from '@/components/chapters/PartialRegenerateModal'
import { useAuthStore } from '@/stores/authStore'
import { useProjectStore } from '@/stores/projectStore'
import { useUiStore } from '@/stores/uiStore'
import type { Chapter, ConflictRow } from '@/types/nf'
import { useRuleLabels } from '@/hooks/useRuleLabels'
import { findRuneSelection, type RuneSelection } from '@/utils/partialRegenerate'

type SeverityFilter = 'all' | ConflictRow['severity']

interface RewriteState extends RuneSelection {
  projectId: string
  chapterId: string
}

const MAX_FALLBACK_SELECTION = 800
const CONFLICTS_PER_PAGE = 20
const SOFT_CHECK_POLL_INTERVAL_MS = 3000
const SOFT_RULE_IDS = ['soft.ooc', 'soft.setting', 'soft.emotion'] as const

type SoftRuleId = (typeof SOFT_RULE_IDS)[number]
type SoftCheckStatus = 'none' | 'running' | 'done_clean' | 'done_flagged' | 'failed'

function isOverridden(row: ConflictRow) {
  return Boolean(row.overridden_at)
}

function getErrorMessage(err: unknown, fallback: string) {
  return friendlyMessage(err, fallback)
}

function findChapterForConflict(chapters: Chapter[], row: ConflictRow): Chapter | null {
  return chapters.find((chapter) => {
    if (row.project_id && chapter.project_id !== row.project_id) return false
    return chapter.chapter_number === row.chapter_no
  }) ?? null
}

function chapterConflictKey(projectId: string | undefined, chapterNo: number) {
  return `${projectId ?? ''}:${chapterNo}`
}

function isSoftRuleId(ruleId: string): ruleId is SoftRuleId {
  return (SOFT_RULE_IDS as readonly string[]).includes(ruleId)
}

function normalizeSoftCheckStatus(status: string | undefined): SoftCheckStatus {
  if (status === 'running' || status === 'done_clean' || status === 'done_flagged' || status === 'failed') return status
  return 'none'
}

function hasRunningSoftCheck(chapters: Chapter[] | undefined) {
  return chapters?.some((chapter) => normalizeSoftCheckStatus(chapter.soft_check_status) === 'running') ?? false
}

function parseConflictDetail(detail: string) {
  try {
    const parsed = JSON.parse(detail) as unknown
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { text: detail, experimental: false }
    }
    const record = parsed as Record<string, unknown>
    const text =
      typeof record.detail === 'string'
        ? record.detail
        : typeof record.message === 'string'
          ? record.message
          : typeof record.description === 'string'
            ? record.description
            : detail
    return { text, experimental: record.experimental === true }
  } catch {
    return { text: detail, experimental: false }
  }
}

function buildRewriteSelection(content: string, row: ConflictRow): RuneSelection | null {
  const candidates = [row.entity_id, row.detail].map((value) => value.trim()).filter(Boolean)
  for (const candidate of candidates) {
    const exact = findRuneSelection(content, candidate)
    if (exact) return exact
  }

  const runes = Array.from(content)
  const start = runes.findIndex((rune) => rune.trim())
  if (start < 0) return null
  const selectedRunes = runes.slice(start, start + MAX_FALLBACK_SELECTION)
  if (!selectedRunes.length) return null
  return {
    start,
    end: start + selectedRunes.length,
    selectedText: selectedRunes.join(''),
  }
}

function usePagination<T>(items: T[], pageSize: number) {
  const [page, setPage] = useState(0)
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize))
  const safePage = Math.min(page, pageCount - 1)
  const pageItems = items.slice(safePage * pageSize, safePage * pageSize + pageSize)
  return { page: safePage, pageCount, setPage, pageItems }
}

function StatBadge({ children, tone }: { children: ReactNode; tone?: 'hard' | 'soft' }) {
  const className =
    tone === 'hard'
      ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300'
      : tone === 'soft'
        ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/35 dark:text-amber-300'
        : undefined
  return (
    <Badge variant={tone ? 'outline' : 'secondary'} className={className}>
      {children}
    </Badge>
  )
}

function SeverityBadge({ severity }: { severity: ConflictRow['severity'] }) {
  const { t } = useTranslation()
  return (
    <Badge
      variant="outline"
      className={
        severity === 'hard'
          ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/70 dark:bg-red-950/35 dark:text-red-300'
          : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/35 dark:text-amber-300'
      }
    >
      {t(`consistencyDashboard.severity.${severity}`)}
    </Badge>
  )
}

export function SoftCheckStatusBadge({
  status,
  count,
  allDisabled,
}: {
  status: string | undefined
  count: number
  allDisabled: boolean
}) {
  const { t } = useTranslation()
  const normalized = allDisabled && normalizeSoftCheckStatus(status) === 'none' ? 'disabled' : normalizeSoftCheckStatus(status)
  const className =
    normalized === 'running'
      ? 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-900/70 dark:bg-sky-950/35 dark:text-sky-300'
      : normalized === 'done_flagged'
        ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/35 dark:text-amber-300'
        : normalized === 'done_clean'
          ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-900/70 dark:bg-green-950/35 dark:text-green-300'
          : normalized === 'failed'
            ? 'border-orange-300 bg-orange-50 text-orange-800 dark:border-orange-900/70 dark:bg-orange-950/35 dark:text-orange-300'
            : 'border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-400'

  return (
    <Badge variant="outline" className={className}>
      {normalized === 'running' ? <Loader2 className="size-3 animate-spin" /> : null}
      {t(`consistencyDashboard.softStatus.${normalized}`, { count })}
    </Badge>
  )
}

function DashboardSkeleton() {
  return (
    <div className="grid gap-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} className="rounded-lg py-0">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-20 rounded-full" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-56" />
                </div>
                <Skeleton className="h-16 w-full" />
              </div>
              <div className="flex flex-wrap gap-2">
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-28" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export default function ConsistencyDashboard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const current = useProjectStore((s) => s.current)
  const currentBranchId = useUiStore((s) => s.currentBranchId)
  const requestSelectChapter = useUiStore((s) => s.requestSelectChapter)
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(current?.id ?? null)
  const [severity, setSeverity] = useState<SeverityFilter>('all')
  const [chapterFilter, setChapterFilter] = useState<number | null>(null)
  const [contextRow, setContextRow] = useState<ConflictRow | null>(null)
  const [overrideRow, setOverrideRow] = useState<ConflictRow | null>(null)
  // 标长线: 卷末硬门的提示直接指向这个操作, 在此之前它只有后端接口、界面里没有入口。
  const [longTermRow, setLongTermRow] = useState<ConflictRow | null>(null)
  const [longTermChapter, setLongTermChapter] = useState('')
  const [overrideReason, setOverrideReason] = useState('')
  // 承诺给出交代的章节。必填 —— 例外是「这笔账我认，第 N 章结清」，不是「别再烦我」。
  const [overrideDueChapter, setOverrideDueChapter] = useState('')
  const [rewriteState, setRewriteState] = useState<RewriteState | null>(null)

  const projectsQuery = useQuery({
    queryKey: ['projects', user?.user_id, 'consistency-dashboard'],
    queryFn: () => projectApi.list(user!.user_id),
    enabled: Boolean(user?.user_id),
  })

  const projects = useMemo(() => projectsQuery.data ?? [], [projectsQuery.data])
  const selectedProject = projects.find((project) => project.id === selectedProjectId) ?? null
  const isAllProjects = selectedProjectId === ALL_PROJECTS_VALUE

  useEffect(() => {
    const fallbackProjectId = current?.id ?? projects[0]?.id ?? null
    setSelectedProjectId((previous) => {
      if (previous === ALL_PROJECTS_VALUE) return previous
      if (previous && projects.some((project) => project.id === previous)) return previous
      if (previous && current?.id === previous) return previous
      return fallbackProjectId
    })
  }, [current?.id, projects])

  const chaptersQuery = useQuery({
    queryKey: ['chapters', selectedProjectId, currentBranchId, 'consistency-dashboard'],
    queryFn: () => chapterApi.list(selectedProjectId!, currentBranchId),
    enabled: Boolean(selectedProjectId),
    refetchInterval: (query) => (hasRunningSoftCheck(query.state.data) ? SOFT_CHECK_POLL_INTERVAL_MS : false),
  })

  // 「未检测」需要区分"功能没开"和"开了还没跑" —— 两者该做的事相反。
  const softSettingsQuery = useQuery({
    queryKey: ['soft-consistency', selectedProjectId],
    queryFn: () => projectApi.getSoftConsistency(selectedProjectId!),
    enabled: Boolean(selectedProjectId) && !isAllProjects,
  })
  const softAllDisabled = isSoftConsistencyAllDisabled(softSettingsQuery.data)

  const conflictsQuery = useQuery({
    queryKey: ['memory-conflicts', selectedProjectId, 'board', currentBranchId],
    queryFn: () => conflictApi.board(selectedProjectId!),
    enabled: Boolean(selectedProjectId),
    refetchInterval: (query) => query.state.data?.chapters.some((chapter) => chapter.detection_status === 'running')
      ? SOFT_CHECK_POLL_INTERVAL_MS : false,
  })

  const ruleLabelsQuery = useRuleLabels()

  const conflicts = conflictsQuery.data?.conflicts ?? []
  const chapters = chaptersQuery.data ?? []
  const ruleLabels = ruleLabelsQuery.data?.rules
  const locale = i18n.resolvedLanguage ?? i18n.language

  const visibleConflicts = useMemo(() => {
    return conflicts.filter((row) => {
      if (severity !== 'all' && row.severity !== severity) return false
      if (chapterFilter && row.chapter_no !== chapterFilter) return false
      return true
    })
  }, [chapterFilter, conflicts, severity])
  const conflictPager = usePagination(visibleConflicts, CONFLICTS_PER_PAGE)

  useEffect(() => {
    conflictPager.setPage(0)
  }, [chapterFilter, conflictPager.setPage, severity])

  const stats = useMemo(
    () => ({
      total: conflicts.length,
      hard: conflicts.filter((row) => row.severity === 'hard').length,
      soft: conflicts.filter((row) => row.severity === 'soft').length,
      overridden: conflicts.filter(isOverridden).length,
    }),
    [conflicts],
  )

  const resetOverrideDialog = () => {
    setOverrideRow(null)
    setOverrideReason('')
    setOverrideDueChapter('')
  }

  const resolveMutation = useMutation({
    mutationFn: ({ row, action }: { row: ConflictRow; action: 'cleanup' | 'recheck' }) =>
      conflictApi.resolve(row.project_id || selectedProjectId!, row.id, action),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['memory-conflicts', selectedProjectId] })
      toast.success(t('consistencyDashboard.reviewResolved'))
    },
    onError: (error) => toast.error(getErrorMessage(error, t('consistencyDashboard.reviewFailed'))),
  })

  const overrideMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProjectId || !overrideRow) return
      const projectId = isAllProjects ? overrideRow.project_id : selectedProjectId
      if (!projectId) return
      await conflictApi.override(projectId, overrideRow.id, overrideReason.trim(), Number(overrideDueChapter))
    },
    onSuccess: async () => {
      toast.success(t('consistencyDashboard.messages.overrideSaved'))
      resetOverrideDialog()
      await queryClient.invalidateQueries({ queryKey: ['memory-conflicts', selectedProjectId] })
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t('common.serviceError')))
    },
  })

  const resetLongTermDialog = () => {
    setLongTermRow(null)
    setLongTermChapter('')
  }

  const longTermMutation = useMutation({
    mutationFn: async () => {
      if (!selectedProjectId || !longTermRow) return
      const projectId = isAllProjects ? longTermRow.project_id : selectedProjectId
      if (!projectId) return
      // 冲突的 entity_id 就是 foreshadow id(见 checkForeshadowVolumeGate)。
      await foreshadowApi.markLongTerm(projectId, longTermRow.entity_id, Number(longTermChapter))
    },
    onSuccess: async () => {
      toast.success(t('consistencyDashboard.longTerm.saved'))
      resetLongTermDialog()
      await queryClient.invalidateQueries({ queryKey: ['memory-conflicts', selectedProjectId] })
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t('common.serviceError')))
    },
  })

  const resubmitMutation = useMutation({
    mutationFn: async (row: ConflictRow) => {
      const chapter = findChapterForConflict(chapters, row)
      if (!chapter) throw new Error(t('consistencyDashboard.messages.chapterMissing'))
      await chapterApi.resubmit(chapter.id)
    },
    onSuccess: async () => {
      toast.success(t('consistencyDashboard.messages.resubmitted'))
      await queryClient.invalidateQueries({ queryKey: ['memory-conflicts', selectedProjectId] })
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, t('common.serviceError')))
    },
  })

  const openChapter = (row: ConflictRow) => {
    if (!selectedProjectId) return
    const chapter = findChapterForConflict(chapters, row)
    if (!chapter) {
      toast.error(t('consistencyDashboard.messages.chapterMissing'))
      return
    }
    const projectId = isAllProjects ? row.project_id || chapter.project_id : selectedProjectId
    if (!projectId) return
    const ok = requestSelectChapter({ projectId, branchId: currentBranchId, chapterId: chapter.id }, () =>
      window.confirm(t('chapter.generate.switchAwayConfirm')),
    )
    if (ok) navigate(`/projects/${encodeURIComponent(projectId)}`)
  }

  const openRewrite = async (row: ConflictRow) => {
    const chapter = findChapterForConflict(chapters, row)
    if (!chapter) {
      toast.error(t('consistencyDashboard.messages.chapterMissing'))
      return
    }
    try {
      const fullChapter = chapter.content ? chapter : await chapterApi.get(chapter.id)
      const content = fullChapter.content ?? ''
      const selection = buildRewriteSelection(content, row)
      if (!selection) {
        toast.warning(t('consistencyDashboard.messages.rewriteNoContent'))
        return
      }
      const projectId = row.project_id || fullChapter.project_id || selectedProjectId
      if (!projectId) return
      setRewriteState({ projectId, chapterId: chapter.id, ...selection })
    } catch (err) {
      toast.error(getErrorMessage(err, t('common.serviceError')))
    }
  }

  const hasNoProjects = projectsQuery.isSuccess && !projects.length

  if (hasNoProjects) {
    return (
      <section className="grid min-h-[calc(100vh-3.5rem)] place-items-center px-4 py-8">
        <div className="rounded-lg border border-dashed bg-card/45 p-8 text-center">
          <p className="text-lg font-medium">{t('consistencyDashboard.noProject')}</p>
        </div>
      </section>
    )
  }

  const isLoading = projectsQuery.isLoading || conflictsQuery.isLoading || chaptersQuery.isLoading || ruleLabelsQuery.isLoading
  const error = projectsQuery.error ?? conflictsQuery.error ?? chaptersQuery.error ?? ruleLabelsQuery.error
  const overrideDueChapterNo = Number(overrideDueChapter)
  const overrideDueValid =
    Number.isInteger(overrideDueChapterNo) &&
    overrideDueChapterNo > 0 &&
    // 期限必须在当前冲突之后 —— 填一个已经过去的章号等于当场逾期，没有意义。
    (!overrideRow || overrideDueChapterNo >= overrideRow.last_chapter)
  const canSaveOverride = Boolean(overrideRow && overrideReason.trim() && overrideDueValid)
  // 长线期限必须【严格晚于】触发章: 填当章或更早等于当场就已逾期, 没有意义。
  const longTermChapterNo = Number(longTermChapter)
  const canSaveLongTerm = Boolean(
    longTermRow &&
      Number.isInteger(longTermChapterNo) &&
      longTermChapterNo > longTermRow.last_chapter,
  )
  const selectedProjectTitle = isAllProjects
    ? t('project.allProjectsAggregate')
    : selectedProject?.title ?? (current?.id === selectedProjectId ? current.title : '')
  const getRuleDisplay = (row: ConflictRow) => {
    if (isSoftRuleId(row.rule_id)) {
      const key = row.rule_id.slice('soft.'.length) as 'ooc' | 'setting' | 'emotion'
      return t(`consistencyDashboard.softRules.${key}`)
    }
    return getRuleLabel(ruleLabels, row.rule_id, locale)
  }

  return (
    <TooltipProvider>
      <section className="px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-normal sm:text-3xl">{t('consistencyDashboard.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              {t('consistencyDashboard.subtitle', { project: selectedProjectTitle })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatBadge tone="hard">{t('consistencyDashboard.stats.hard', { count: stats.hard })}</StatBadge>
            <StatBadge tone="soft">{t('consistencyDashboard.stats.soft', { count: stats.soft })}</StatBadge>
            <StatBadge>{t('consistencyDashboard.stats.overridden', { count: stats.overridden })}</StatBadge>
            <Button variant="outline" onClick={() => void conflictsQuery.refetch()} disabled={conflictsQuery.isFetching}>
              <RefreshCw className={conflictsQuery.isFetching ? 'size-4 animate-spin' : 'size-4'} />
              {t('common.retry')}
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-end">
          <div className="space-y-2">
            <Label>{t('consistencyDashboard.projectPicker.label')}</Label>
            <ProjectCombobox
              projects={projects}
              value={selectedProjectId ?? ''}
              onChange={(value) => {
                setSelectedProjectId(value || null)
                setChapterFilter(null)
              }}
              isLoading={projectsQuery.isLoading}
              className="w-full sm:w-[260px]"
              ariaLabel={t('consistencyDashboard.projectPicker.placeholder')}
              includeAllProjects
            />
          </div>
          <div className="space-y-2">
            <Label>{t('consistencyDashboard.filters.severity')}</Label>
            <Select value={severity} onValueChange={(value) => setSeverity(value as SeverityFilter)}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('consistencyDashboard.filters.all')}</SelectItem>
                <SelectItem value="hard">{t('consistencyDashboard.severity.hard')}</SelectItem>
                <SelectItem value="soft">{t('consistencyDashboard.severity.soft')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="consistency-chapter-filter">{t('consistencyDashboard.filters.chapter')}</Label>
            <NumberInput
              id="consistency-chapter-filter"
              min={1}
              value={chapterFilter}
              placeholder={t('consistencyDashboard.filters.chapterPlaceholder')}
              onChange={(value) => setChapterFilter(typeof value === 'number' && value >= 1 ? value : null)}
              className="w-full sm:w-[220px]"
            />
          </div>
          {chapterFilter ? (
            <Button variant="outline" onClick={() => setChapterFilter(null)}>
              {t('consistencyDashboard.filters.clearChapter')}
            </Button>
          ) : null}
        </div>

        {error ? (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <div>
                  <p className="font-medium">{t('consistencyDashboard.loadFailed')}</p>
                  <p className="mt-1 text-destructive/90">{getErrorMessage(error, t('common.serviceError'))}</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => void conflictsQuery.refetch()}>
                {t('common.retry')}
              </Button>
            </div>
          </div>
        ) : isLoading ? (
          <DashboardSkeleton />
        ) : (
          <div className="space-y-3">
            {conflictsQuery.data?.chapters.length ? (
              <div className="rounded-lg border bg-card p-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h2 className="text-sm font-medium">{t('consistencyDashboard.softStatus.title')}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(conflictsQuery.data?.chapters ?? []).filter((chapter) => !chapterFilter || chapter.chapter_no === chapterFilter).map((chapter) => (
                    <div
                      key={chapterConflictKey(chapter.project_id, chapter.chapter_no)}
                      className="flex min-h-8 flex-wrap items-center gap-2 rounded-md border bg-background px-2.5 py-1.5"
                    >
                      <span className="text-sm text-muted-foreground">
                        {isAllProjects ? `${projects.find((project) => project.id === chapter.project_id)?.title || chapter.project_id} · ` : ''}
                        {t('consistencyDashboard.chapterNo', { chapter: chapter.chapter_no })}
                      </span>
                      <SoftCheckStatusBadge
                        status={chapter.status}
                        count={chapter.count}
                        allDisabled={softAllDisabled}
                      />
                      {chapter.recorded_at ? <span className="text-xs text-muted-foreground">{t('consistencyDashboard.recordedAt', { time: chapter.recorded_at })}</span> : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            {visibleConflicts.length ? (
              <div className="grid gap-3">
                {conflictPager.pageItems.map((row) => {
                  const overridden = isOverridden(row)
                  const detail = parseConflictDetail(row.detail)
                  return (
                    <Card
                      key={row.id}
                      className={`rounded-lg py-0 ${overridden ? 'opacity-60' : ''} ${
                        row.severity === 'hard'
                          ? 'border-red-200 dark:border-red-900/60'
                          : 'border-amber-200 dark:border-amber-900/60'
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1">
                            <div className="mb-2 flex flex-wrap gap-2">
                              {isAllProjects ? (
                                <Badge variant="outline">{row.project_title || row.project_id || t('project.allProjectsAggregate')}</Badge>
                              ) : null}
                              <Badge variant="secondary">{t('consistencyDashboard.chapterNo', { chapter: row.chapter_no })}</Badge>
                              <SeverityBadge severity={row.severity} />
                              {row.resolution_reason === 'reference_invalid' ? <Badge variant="outline">{t('consistencyDashboard.referenceInvalid')}</Badge> : null}
                              {detail.experimental ? (
                                <Badge
                                  variant="outline"
                                  className="border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-900/70 dark:bg-violet-950/35 dark:text-violet-300"
                                >
                                  {t('consistencyDashboard.experimentalBadge')}
                                </Badge>
                              ) : null}
                              {overridden ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Badge variant="outline" className="cursor-help text-muted-foreground">
                                      {t('consistencyDashboard.override.overridden')}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>{row.override_reason || t('consistencyDashboard.override.noReason')}</TooltipContent>
                                </Tooltip>
                              ) : null}
                            </div>
                            <div className="grid gap-2 text-sm md:grid-cols-2">
                              <p>
                                <span className="font-medium">{t('consistencyDashboard.fields.rule')}</span> {getRuleDisplay(row)}
                              </p>
                              <p>
                                <span className="font-medium">{t('consistencyDashboard.fields.entity')}</span> {row.entity_name || row.entity_id}
                              </p>
                            </div>
                            <p className="mt-3 whitespace-pre-wrap text-sm leading-6">{detail.text}</p>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {row.resolution_reason === 'reference_invalid' ? (
                              <Button variant="outline" disabled={resolveMutation.isPending} onClick={() => {
                                if (window.confirm(t('consistencyDashboard.cleanupConfirm'))) resolveMutation.mutate({ row, action: 'cleanup' })
                              }}>{t('consistencyDashboard.cleanupReference')}</Button>
                            ) : row.rule_id === 'location_exists' && row.detail.includes('尚未登记') ? (
                              <Button variant="outline" disabled={resolveMutation.isPending} onClick={() => {
                                if (window.confirm(t('consistencyDashboard.recheckConfirm'))) resolveMutation.mutate({ row, action: 'recheck' })
                              }}>{t('consistencyDashboard.recheckRegistration')}</Button>
                            ) : null}
                            <Button variant="outline" onClick={() => setContextRow(row)}>
                              <ScanSearch className="size-4" />
                              {t('consistencyDashboard.actions.viewContext')}
                            </Button>
                            <Button variant="outline" disabled={overridden} onClick={() => setOverrideRow(row)}>
                              <AlertTriangle className="size-4" />
                              {t('consistencyDashboard.actions.override')}
                            </Button>
                            {['foreshadow_volume_gate', 'foreshadow_payoff'].includes(row.rule_id) ? (
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setLongTermRow(row)
                                  setLongTermChapter('')
                                }}
                              >
                                <Clock className="size-4" />
                                {t('consistencyDashboard.actions.markLongTerm')}
                              </Button>
                            ) : null}
                            <Button variant="outline" title={t('consistencyDashboard.actions.openChapterTitle')} onClick={() => openChapter(row)}>
                              <Pencil className="size-4" />
                              {t('consistencyDashboard.actions.openChapter')}
                            </Button>
                            <Button
                              variant="outline"
                              onClick={() => resubmitMutation.mutate(row)}
                              disabled={resubmitMutation.isPending}
                            >
                              <RotateCcw className={resubmitMutation.isPending ? 'size-4 animate-spin' : 'size-4'} />
                              {t('consistencyDashboard.actions.resubmit')}
                            </Button>
                            <Button onClick={() => void openRewrite(row)}>
                              <Zap className="size-4" />
                              {t('consistencyDashboard.actions.partialRewrite')}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
                <Pagination
                  page={conflictPager.page}
                  pageCount={conflictPager.pageCount}
                  pageSize={CONFLICTS_PER_PAGE}
                  total={visibleConflicts.length}
                  onPage={conflictPager.setPage}
                />
              </div>
            ) : (
              <div className="rounded-lg border border-green-300 bg-green-50 px-6 py-10 text-center text-green-800 dark:border-green-900 dark:bg-green-950/30 dark:text-green-200">
                <CheckCircle className="mx-auto size-8" />
                <h2 className="mt-3 text-xl font-semibold tracking-normal">
                  {stats.total === 0 ? t('consistencyDashboard.empty.title') : t('consistencyDashboard.empty.filteredTitle')}
                </h2>
                <p className="mt-2 text-sm">
                  {stats.total === 0 ? t('consistencyDashboard.empty.description') : t('consistencyDashboard.empty.filteredDescription')}
                </p>
              </div>
            )}
          </div>
        )}

        <Dialog
          open={Boolean(overrideRow)}
          onOpenChange={(open) => {
            if (!open) resetOverrideDialog()
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('consistencyDashboard.override.title')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/70 dark:bg-amber-950/35 dark:text-amber-300">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <p>{t('consistencyDashboard.override.warning')}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOverrideReason(t('consistencyDashboard.override.reasons.intentionalLongArc'))}
                >
                  {t('consistencyDashboard.override.reasons.intentionalLongArc')}
                </Button>
              </div>
              <textarea
                rows={4}
                maxLength={500}
                value={overrideReason}
                placeholder={t('consistencyDashboard.override.placeholder')}
                onChange={(event) => setOverrideReason(event.target.value)}
                className="min-h-24 w-full resize-y rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30"
              />
              <p className="text-right text-xs text-muted-foreground">{overrideReason.length}/500</p>
              <div className="space-y-1.5">
                <label className="text-sm font-medium" htmlFor="override-due-chapter">
                  {t('consistencyDashboard.override.dueChapterLabel')}
                </label>
                <input
                  id="override-due-chapter"
                  type="number"
                  min={overrideRow ? overrideRow.last_chapter : 1}
                  value={overrideDueChapter}
                  placeholder={t('consistencyDashboard.override.dueChapterPlaceholder')}
                  onChange={(event) => setOverrideDueChapter(event.target.value)}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
                />
                <p className="text-xs text-muted-foreground">
                  {t('consistencyDashboard.override.dueChapterHint')}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={resetOverrideDialog}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={!canSaveOverride || overrideMutation.isPending}
                onClick={() => void overrideMutation.mutate()}
              >
                {overrideMutation.isPending ? <RefreshCw className="size-4 animate-spin" /> : null}
                {t('consistencyDashboard.override.submit')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {/* 标长线: 卷末硬门提示「或标记为长线伏笔并把回收章节推到后面的卷」——
          这个对话框就是那句话的出口。没有它, 硬门指向的动作在界面里不存在。 */}
      <Dialog open={Boolean(longTermRow)} onOpenChange={(open) => (open ? null : resetLongTermDialog())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('consistencyDashboard.longTerm.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-3 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800 dark:border-sky-900/70 dark:bg-sky-950/35 dark:text-sky-300">
              <Clock className="mt-0.5 size-4 shrink-0" />
              <p>{t('consistencyDashboard.longTerm.hint')}</p>
            </div>
            {longTermRow ? (
              <p className="rounded-md border bg-background/40 p-3 text-sm leading-6">
                {longTermRow.entity_name || longTermRow.entity_id}
              </p>
            ) : null}
            <div className="space-y-1.5">
              <label className="text-sm font-medium" htmlFor="long-term-chapter">
                {t('consistencyDashboard.longTerm.chapterLabel')}
              </label>
              <input
                id="long-term-chapter"
                type="number"
                min={longTermRow ? longTermRow.last_chapter + 1 : 1}
                value={longTermChapter}
                placeholder={t('consistencyDashboard.longTerm.chapterPlaceholder')}
                onChange={(event) => setLongTermChapter(event.target.value)}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30"
              />
              <p className="text-xs text-muted-foreground">{t('consistencyDashboard.longTerm.chapterHint')}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetLongTermDialog}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={!canSaveLongTerm || longTermMutation.isPending}
              onClick={() => void longTermMutation.mutate()}
            >
              {longTermMutation.isPending ? <RefreshCw className="size-4 animate-spin" /> : null}
              {t('consistencyDashboard.longTerm.submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        <PartialRegenerateModal
          open={Boolean(rewriteState)}
          projectId={rewriteState?.projectId ?? null}
          contentLanguage={projects.find((project) => project.id === rewriteState?.projectId)?.content_language}
          chapterId={rewriteState?.chapterId ?? null}
          selectedText={rewriteState?.selectedText ?? ''}
          start={rewriteState?.start ?? 0}
          end={rewriteState?.end ?? 0}
          onClose={() => setRewriteState(null)}
          onApplied={async () => {
            await chaptersQuery.refetch()
            await conflictsQuery.refetch()
          }}
        />
        <ConflictContextDialog
          open={Boolean(contextRow)}
          row={contextRow}
          chapters={chapters}
          onClose={() => setContextRow(null)}
        />
      </section>
    </TooltipProvider>
  )
}
